<template>
  <div class="emails-page">
    <div class="page-header">
      <div class="header-content">
        <h1>
          <i class="fas fa-envelope"></i>
          Emails
        </h1>
        <p>Gestion et analyse des emails commerciaux</p>
      </div>
      <div class="header-actions">
        <div class="search-box">
          <i class="fas fa-search"></i>
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="Rechercher un email..."
            class="search-input"
          >
        </div>
        <select v-model="statusFilter" class="status-filter">
          <option value="">Tous les statuts</option>
          <option value="waiting">Message en attente d'analyse</option>
          <option value="pending">Message analysé mais en attente de traitement</option>
          <option value="to_treat">Message à traiter manuellement</option>
          <option value="processed">Message traité avec succès</option>
        </select>
        <select v-model="accountFilter" class="account-filter">
          <option value="">Tous les comptes</option>
          <option v-for="account in emailAccounts" :key="account.id" :value="account.id">
            {{ account.email }}
          </option>
        </select>
        <button class="btn btn-primary" @click="syncEmails" :disabled="syncing">
          <i class="fas fa-sync" :class="{ 'fa-spin': syncing }"></i>
          Synchroniser
        </button>
      </div>
    </div>

    <div class="stats-cards">
      <div class="stat-card">
        <div class="stat-icon waiting">
          <i class="fas fa-inbox"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats.waiting }}</h3>
          <p>En attente d'analyse</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon pending">
          <i class="fas fa-clock"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats.pending }}</h3>
          <p>En attente de traitement</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon to-treat">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats.to_treat }}</h3>
          <p>À traiter manuellement</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon processed">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats.processed }}</h3>
          <p>Traités avec succès</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon total">
          <i class="fas fa-chart-bar"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats.total }}</h3>
          <p>Total</p>
        </div>
      </div>
    </div>

    <div class="emails-table">
      <div class="table-header">
        <h2>Liste des emails</h2>
        <div class="table-actions">
          <button class="btn btn-outline" @click="refreshEmails">
            <i class="fas fa-sync" :class="{ 'fa-spin': loading }"></i>
            Actualiser
          </button>
          <button class="btn btn-outline" @click="showFilters = !showFilters">
            <i class="fas fa-filter"></i>
            Filtres
          </button>
        </div>
      </div>

      <!-- Filtres avancés -->
      <div v-if="showFilters" class="filters-panel">
        <div class="filters-grid">
          <div class="filter-group">
            <label>Date de début:</label>
            <input v-model="filters.dateFrom" type="date" class="filter-input">
          </div>
          <div class="filter-group">
            <label>Date de fin:</label>
            <input v-model="filters.dateTo" type="date" class="filter-input">
          </div>
          <div class="filter-group">
            <label>Expéditeur:</label>
            <input v-model="filters.sender" type="text" placeholder="Email expéditeur" class="filter-input">
          </div>
          <div class="filter-group">
            <label>Contient:</label>
            <input v-model="filters.content" type="text" placeholder="Mots-clés" class="filter-input">
          </div>
        </div>
        <div class="filters-actions">
          <button class="btn btn-primary" @click="applyFilters">
            <i class="fas fa-search"></i>
            Appliquer
          </button>
          <button class="btn btn-outline" @click="clearFilters">
            <i class="fas fa-times"></i>
            Effacer
          </button>
        </div>
      </div>

      <div class="table-container table-responsive">
        <table class="data-table">
          <thead>
            <tr>
              <th>Sujet</th>
              <th>Expéditeur</th>
              <th>Compte</th>
              <th>Date</th>
              <th>Statut</th>
              <th>IA Score</th>
              <th>Pièces jointes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="email in filteredEmails" :key="email.id" class="table-row" :class="{ 'unread': !email.read }">
              <td class="subject" data-label="Sujet">
                <div class="subject-content">
                  <span class="subject-text" :class="{ 'font-bold': !email.read }">{{ email.subject }}</span>
                  <div class="email-preview">{{ email.preview }}</div>
                </div>
              </td>
              <td class="sender" data-label="Expéditeur">
                <div class="sender-info">
                  <div class="sender-name">{{ email.senderName }}</div>
                  <div class="sender-email">{{ email.senderEmail }}</div>
                </div>
              </td>
              <td class="account" data-label="Compte">
                <span class="account-badge">{{ getAccountName(email.accountId) }}</span>
              </td>
              <td class="date" data-label="Date">
                <div class="date-info">
                  <div class="date">{{ formatDate(email.receivedAt) }}</div>
                  <div class="time">{{ formatTime(email.receivedAt) }}</div>
                </div>
              </td>
              <td class="status" data-label="Statut">
                <span class="status-badge" :class="getStatusClass(email.status)">
                  {{ getStatusText(email.status) }}
                </span>
              </td>
              <td class="ai-score" data-label="IA Score">
                <div class="score-container">
                  <div class="score-bar">
                    <div 
                      class="score-fill" 
                      :style="{ width: email.aiScore + '%' }"
                      :class="getScoreClass(email.aiScore)"
                    ></div>
                  </div>
                  <span class="score-text">{{ email.aiScore }}%</span>
                </div>
              </td>
              <td class="attachments" data-label="Pièces jointes">
                <div v-if="email.attachments.length > 0" class="attachments-info">
                  <i class="fas fa-paperclip"></i>
                  <span>{{ email.attachments.length }}</span>
                </div>
                <span v-else class="no-attachments">-</span>
              </td>
              <td class="actions" data-label="Actions">
                <button 
                  class="btn btn-sm btn-primary" 
                  @click="viewEmail(email)"
                  title="Voir l'email"
                >
                  <i class="fas fa-eye"></i>
                </button>
                <button 
                  v-if="email.status === 'waiting'"
                  class="btn btn-sm btn-success" 
                  @click="processEmail(email)"
                  title="Traiter avec IA"
                >
                  <i class="fas fa-magic"></i>
                </button>
                <button 
                  class="btn btn-sm btn-outline" 
                  @click="markAsRead(email)"
                  :title="email.read ? 'Marquer comme non lu' : 'Marquer comme lu'"
                >
                  <i :class="email.read ? 'fas fa-envelope' : 'fas fa-envelope-open'"></i>
                </button>
                <button 
                  class="btn btn-sm btn-danger" 
                  @click="deleteEmail(email)"
                  title="Supprimer"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="filteredEmails.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-envelope"></i>
        </div>
        <h3>Aucun email trouvé</h3>
        <p>{{ searchQuery ? 'Aucun email ne correspond à votre recherche.' : 'Aucun email n\'a été synchronisé pour le moment.' }}</p>
      </div>
    </div>

    <!-- Modal de visualisation d'email -->
    <div v-if="selectedEmail" class="modal-overlay" @click="closeModal">
      <div class="modal-content large" @click.stop>
        <div class="modal-header">
          <h2>
            <i class="fas fa-envelope"></i>
            {{ selectedEmail.subject }}
          </h2>
          <button class="modal-close" @click="closeModal" aria-label="Fermer">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="email-details">
            <div class="email-meta">
              <div class="meta-grid">
                <div class="meta-item">
                  <label>De:</label>
                  <span>{{ selectedEmail.senderName }} &lt;{{ selectedEmail.senderEmail }}&gt;</span>
                </div>
                <div class="meta-item">
                  <label>À:</label>
                  <span>{{ selectedEmail.recipientEmail }}</span>
                </div>
                <div class="meta-item">
                  <label>Date:</label>
                  <span>{{ formatDateTime(selectedEmail.receivedAt) }}</span>
                </div>
                <div class="meta-item">
                  <label>Compte:</label>
                  <span>{{ getAccountName(selectedEmail.accountId) }}</span>
                </div>
                <div class="meta-item">
                  <label>Statut:</label>
                  <span class="status-badge" :class="getStatusClass(selectedEmail.status)">
                    {{ getStatusText(selectedEmail.status) }}
                  </span>
                </div>
                <div class="meta-item">
                  <label>Score IA:</label>
                  <span class="score-badge" :class="getScoreClass(selectedEmail.aiScore)">
                    {{ selectedEmail.aiScore }}%
                  </span>
                </div>
              </div>
            </div>

            <div v-if="selectedEmail.attachments.length > 0" class="attachments-section">
              <h3>Pièces jointes ({{ selectedEmail.attachments.length }})</h3>
              <div class="attachments-list">
                <div v-for="attachment in selectedEmail.attachments" :key="attachment.id" class="attachment-item">
                  <div class="attachment-icon">
                    <i :class="getAttachmentIcon(attachment.type)"></i>
                  </div>
                  <div class="attachment-info">
                    <div class="attachment-name">{{ attachment.name }}</div>
                    <div class="attachment-size">{{ formatFileSize(attachment.size) }}</div>
                  </div>
                  <button class="btn btn-sm btn-outline" @click="downloadAttachment(attachment)" title="Télécharger">
                    <i class="fas fa-download"></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="email-content">
              <h3>Contenu</h3>
              <div class="content-body" v-html="selectedEmail.htmlContent || selectedEmail.textContent"></div>
            </div>

            <div v-if="selectedEmail.extractedData" class="extracted-data">
              <h3>Données extraites par IA</h3>
              <div class="extracted-content">
                <pre>{{ JSON.stringify(selectedEmail.extractedData, null, 2) }}</pre>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            Fermer
          </button>
          <button 
            v-if="selectedEmail.status === 'waiting'"
            type="button" 
            class="btn btn-success" 
            @click="processEmail(selectedEmail)"
          >
            <i class="fas fa-magic"></i>
            Traiter avec IA
          </button>
          <button 
            type="button" 
            class="btn btn-primary" 
            @click="replyToEmail(selectedEmail)"
          >
            <i class="fas fa-reply"></i>
            Répondre
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useEmailsStore } from '../stores/emails'
import { useEmailAccountsStore } from '../stores/emailAccounts'

export default {
  name: 'Emails',
  setup() {
    const emailsStore = useEmailsStore()
    const emailAccountsStore = useEmailAccountsStore()
    
    const loading = ref(false)
    const syncing = ref(false)
    const searchQuery = ref('')
    const statusFilter = ref('')
    const accountFilter = ref('')
    const selectedEmail = ref(null)
    const showFilters = ref(false)
    
    const filters = reactive({
      dateFrom: '',
      dateTo: '',
      sender: '',
      content: ''
    })
    
    const stats = computed(() => emailsStore.stats)
    const emails = computed(() => emailsStore.emails)
    const emailAccounts = computed(() => emailAccountsStore.accounts)
    
    const filteredEmails = computed(() => {
      let filtered = emails.value
      
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(email => 
          email.subject.toLowerCase().includes(query) ||
          email.senderName.toLowerCase().includes(query) ||
          email.senderEmail.toLowerCase().includes(query) ||
          email.textContent.toLowerCase().includes(query)
        )
      }
      
      if (statusFilter.value) {
        filtered = filtered.filter(email => email.status === statusFilter.value)
      }
      
      if (accountFilter.value) {
        filtered = filtered.filter(email => email.accountId === parseInt(accountFilter.value))
      }
      
      return filtered
    })

    // Synchroniser le filtre de statut avec le store et recharger
    watch(statusFilter, async (val) => {
      emailsStore.setFilters({ status: val || '' })
      await emailsStore.loadEmails()
    })

    // Synchroniser le filtre de compte avec le store et recharger
    watch(accountFilter, async (val) => {
      const accId = val ? parseInt(val) : null
      emailsStore.setFilters({ accountId: accId })
      await emailsStore.loadEmails()
    })
    
    const getStatusClass = (status) => {
      switch (status) {
        case 'waiting': return 'status-waiting'
        case 'pending': return 'status-pending'
        case 'to_treat': return 'status-to-treat'
        case 'processed': return 'status-processed'
        default: return 'status-unknown'
      }
    }
    
    const getStatusText = (status) => {
      switch (status) {
        case 'waiting': return 'Message en attente d\'analyse'
        case 'pending': return 'Message analysé mais en attente de traitement'
        case 'to_treat': return 'Message à traiter manuellement'
        case 'processed': return 'Message traité avec succès'
        default: return 'Inconnu'
      }
    }
    
    const getScoreClass = (score) => {
      if (score >= 80) return 'score-high'
      if (score >= 60) return 'score-medium'
      return 'score-low'
    }
    
    const getAccountName = (accountId) => {
      const account = emailAccounts.value.find(acc => acc.id === accountId)
      return account ? account.email : 'Compte inconnu'
    }
    
    const getAttachmentIcon = (type) => {
      if (type.includes('pdf')) return 'fas fa-file-pdf'
      if (type.includes('image')) return 'fas fa-file-image'
      if (type.includes('word')) return 'fas fa-file-word'
      if (type.includes('excel')) return 'fas fa-file-excel'
      if (type.includes('zip')) return 'fas fa-file-archive'
      return 'fas fa-file'
    }
    
    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('fr-FR')
    }
    
    const formatTime = (date) => {
      return new Date(date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    }
    
    const formatDateTime = (date) => {
      return new Date(date).toLocaleString('fr-FR')
    }
    
    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
    
    const refreshEmails = async () => {
      loading.value = true
      try {
        await emailsStore.loadEmails()
      } finally {
        loading.value = false
      }
    }
    
    const syncEmails = async () => {
      syncing.value = true
      try {
        await emailsStore.syncEmails()
      } finally {
        syncing.value = false
      }
    }
    
    const viewEmail = (email) => {
      selectedEmail.value = email
      if (!email.read) {
        markAsRead(email)
      }
    }
    
    const closeModal = () => {
      selectedEmail.value = null
    }
    
    const processEmail = async (email) => {
      try {
        await emailsStore.processEmailWithAI(email.id)
      } catch (error) {
        console.error('Erreur traitement IA:', error)
      }
    }
    
    const markAsRead = async (email) => {
      try {
        await emailsStore.markAsRead(email.id, !email.read)
      } catch (error) {
        console.error('Erreur marquage lu/non lu:', error)
      }
    }
    
    const deleteEmail = async (email) => {
      if (confirm('Êtes-vous sûr de vouloir supprimer cet email ?')) {
        try {
          await emailsStore.deleteEmail(email.id)
        } catch (error) {
          console.error('Erreur suppression:', error)
        }
      }
    }
    
    const downloadAttachment = (attachment) => {
      // TODO: Implémenter le téléchargement
      console.log('Télécharger:', attachment)
    }
    
    const replyToEmail = (email) => {
      // TODO: Implémenter la réponse
      console.log('Répondre à:', email)
    }
    
    const applyFilters = () => {
      emailsStore.setFilters(filters)
      showFilters.value = false
    }
    
    const clearFilters = () => {
      Object.keys(filters).forEach(key => {
        filters[key] = ''
      })
      emailsStore.clearFilters()
    }
    
    onMounted(() => {
      emailsStore.loadEmails()
      emailAccountsStore.loadAccounts()
    })
    
    return {
      loading,
      syncing,
      searchQuery,
      statusFilter,
      accountFilter,
      selectedEmail,
      showFilters,
      filters,
      stats,
      emails,
      emailAccounts,
      filteredEmails,
      getStatusClass,
      getStatusText,
      getScoreClass,
      getAccountName,
      getAttachmentIcon,
      formatDate,
      formatTime,
      formatDateTime,
      formatFileSize,
      refreshEmails,
      syncEmails,
      viewEmail,
      closeModal,
      processEmail,
      markAsRead,
      deleteEmail,
      downloadAttachment,
      replyToEmail,
      applyFilters,
      clearFilters
    }
  }
}
</script>

<style scoped>
.emails-page {
  padding: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.header-content h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}

.header-content h1 i {
  margin-right: 0.75rem;
  color: #6366f1;
}

.header-content p {
  color: #6b7280;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-box i {
  position: absolute;
  left: 1rem;
  color: #6b7280;
}

.search-input {
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  width: 300px;
}

.status-filter,
.account-filter {
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  min-width: 150px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-size: 1.25rem;
  color: white;
}

.stat-icon.waiting {
  background: #f59e0b;
}

.stat-icon.pending {
  background: #3b82f6;
}

.stat-icon.to-treat {
  background: #ef4444;
}

.stat-icon.processed {
  background: #10b981;
}

.stat-icon.total {
  background: #6366f1;
}

.stat-content h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
}

.stat-content p {
  color: #6b7280;
  margin: 0;
}

.emails-table {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.table-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.table-actions {
  display: flex;
  gap: 0.5rem;
}

.filters-panel {
  padding: 1.5rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
}

.filter-group label {
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.filter-input {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
}

.filters-actions {
  display: flex;
  gap: 0.5rem;
}

.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: #f9fafb;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.data-table td {
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
}

.table-row:hover {
  background: #f9fafb;
}

.table-row.unread {
  background: #fef3c7;
}

.subject-content {
  max-width: 300px;
}

.subject-text {
  display: block;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.subject-text.font-bold {
  font-weight: 700;
}

.email-preview {
  color: #6b7280;
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sender-name {
  font-weight: 600;
  color: #1f2937;
}

.sender-email {
  color: #6b7280;
  font-size: 0.875rem;
}

.account-badge {
  background: #e0e7ff;
  color: #3730a3;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 500;
  font-size: 0.875rem;
}

.date {
  font-weight: 500;
  color: #1f2937;
}

.time {
  color: #6b7280;
  font-size: 0.875rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-waiting {
  background: #fef3c7;
  color: #92400e;
}

.status-pending {
  background: #dbeafe;
  color: #1e40af;
}

.status-to-treat {
  background: #fee2e2;
  color: #dc2626;
}

.status-processed {
  background: #dcfce7;
  color: #166534;
}

.score-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.score-bar {
  width: 60px;
  height: 16px;
  background: #f3f4f6;
  border-radius: 8px;
  overflow: hidden;
}

.score-fill {
  height: 100%;
  border-radius: 8px;
  transition: width 0.3s ease;
}

.score-high {
  background: #10b981;
}

.score-medium {
  background: #f59e0b;
}

.score-low {
  background: #ef4444;
}

.score-text {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
}

.attachments-info {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #6b7280;
}

.no-attachments {
  color: #9ca3af;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.btn i {
  margin-right: 0.5rem;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

.btn-sm i {
  margin-right: 0;
}

.btn-primary {
  background: #6366f1;
  color: white;
}

.btn-success {
  background: #10b981;
  color: white;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-outline {
  background: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
}

.btn-secondary {
  background: #6b7280;
  color: white;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2rem;
  color: #9ca3af;
}

.empty-state h3 {
  font-size: 1.5rem;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: #6b7280;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content.large {
  max-width: 1000px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
}

.modal-header h2 i {
  margin-right: 0.75rem;
  color: #6366f1;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.email-meta {
  margin-bottom: 2rem;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.meta-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 6px;
}

.meta-item label {
  font-weight: 600;
  color: #374151;
}

.meta-item span {
  color: #1f2937;
}

.attachments-section {
  margin-bottom: 2rem;
}

.attachments-section h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
}

.attachments-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.attachment-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.attachment-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e5e7eb;
  border-radius: 6px;
  margin-right: 1rem;
  color: #6b7280;
}

.attachment-info {
  flex: 1;
}

.attachment-name {
  font-weight: 500;
  color: #1f2937;
}

.attachment-size {
  color: #6b7280;
  font-size: 0.875rem;
}

.email-content {
  margin-bottom: 2rem;
}

.email-content h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
}

.content-body {
  padding: 1rem;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  max-height: 400px;
  overflow-y: auto;
}

.extracted-data {
  margin-bottom: 2rem;
}

.extracted-data h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
}

.extracted-content {
  background: #1f2937;
  color: #f9fafb;
  padding: 1rem;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  max-height: 300px;
  overflow-y: auto;
}

.score-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.875rem;
}

/* ---------- Responsive ---------- */

/* Les chaînes longues (sujets, emails, n° PO) ne doivent jamais élargir la page */
.attachment-name,
.meta-item span {
  overflow-wrap: anywhere;
}

.attachment-info {
  min-width: 0;
}

.content-body {
  overflow-wrap: anywhere;
}

.content-body :deep(img),
.content-body :deep(table) {
  max-width: 100%;
  height: auto;
}

.extracted-content pre {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: inherit;
}

@media (max-width: 991.98px) {
  .emails-page {
    padding: 0;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .header-actions {
    width: 100%;
    gap: 0.75rem;
  }

  .search-box {
    flex: 1 1 260px;
    min-width: 0;
  }

  .search-input {
    width: 100%;
    min-width: 0;
  }

  /* Un <select> prend la largeur de sa plus longue option : on le borne */
  .status-filter,
  .account-filter {
    flex: 1 1 200px;
    min-width: 0;
    max-width: 100%;
    text-overflow: ellipsis;
  }

  .stats-cards {
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .table-header {
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
  }

  .table-actions,
  .filters-actions {
    flex-wrap: wrap;
  }

  .filters-panel {
    padding: 1rem 1.25rem;
  }

  .modal-header h2 {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .modal-header {
    gap: 0.75rem;
  }

  .modal-close {
    flex-shrink: 0;
  }
}

@media (max-width: 767.98px) {
  .header-content h1 {
    font-size: 1.6rem;
  }

  .header-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box,
  .status-filter,
  .account-filter {
    flex: 0 0 auto;
    width: 100%;
  }

  .header-actions > .btn {
    justify-content: center;
  }

  /* KPI : 2 colonnes, la dernière carte impaire prend toute la largeur */
  .stats-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .stats-cards .stat-card:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }

  .stat-card {
    padding: 1rem;
    min-width: 0;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    margin-right: 0.75rem;
    font-size: 1rem;
  }

  .stat-content {
    min-width: 0;
  }

  .stat-content h3 {
    font-size: 1.25rem;
  }

  .stat-content p {
    font-size: 0.8rem;
    line-height: 1.3;
  }

  .filters-grid {
    grid-template-columns: 1fr;
  }

  /* Tableau -> cartes empilées (le tableau est le contenu principal de la page) */
  .table-container {
    overflow-x: visible;
  }

  .data-table,
  .data-table tbody {
    display: block;
    width: 100%;
  }

  .data-table thead {
    display: none;
  }

  .data-table tr.table-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem 1rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .data-table td {
    display: block;
    padding: 0;
    border: none;
    min-width: 0;
  }

  .data-table td::before {
    content: attr(data-label);
    display: block;
    margin-bottom: 0.2rem;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #6b7280;
  }

  .data-table td.subject,
  .data-table td.sender,
  .data-table td.actions {
    grid-column: 1 / -1;
  }

  .data-table td.subject::before {
    display: none;
  }

  .subject-content {
    max-width: none;
  }

  .subject-text,
  .sender-name,
  .sender-email {
    overflow-wrap: anywhere;
  }

  .data-table td.actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding-top: 0.25rem;
  }

  .data-table td.actions::before {
    display: none;
  }

  .status-badge {
    display: inline-block;
    line-height: 1.4;
  }

  .account-badge {
    display: inline-block;
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  /* Modal plein écran */
  .modal-overlay {
    align-items: stretch;
  }

  .modal-content,
  .modal-content.large {
    width: 100%;
    max-width: none;
    height: 100vh;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header {
    padding: 1rem 1.25rem;
    padding-top: max(1rem, env(safe-area-inset-top));
  }

  .modal-header h2 {
    font-size: 1.15rem;
    align-items: flex-start;
  }

  .modal-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 1.25rem;
  }

  .modal-footer {
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 1rem 1.25rem;
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }

  .modal-footer .btn {
    flex: 1 1 auto;
    justify-content: center;
  }

  .meta-grid {
    grid-template-columns: 1fr;
  }

  .meta-item {
    gap: 0.75rem;
  }

  .meta-item span {
    min-width: 0;
    text-align: right;
  }
}

@media (max-width: 359.98px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }
}

/* Cibles tactiles >= 44px pour les boutons icône */
@media (pointer: coarse) {
  .btn-sm,
  .modal-close {
    min-width: 44px;
    min-height: 44px;
    justify-content: center;
  }
}
</style>